// Validación de los datos de la cuenta ANTES de pedirle la clave al banco.
//
// BNC solo manda 10 claves por día y por cliente: un teléfono mal escrito no
// devuelve "dato inválido", devuelve una clave quemada que además le llega a otra
// persona. Las reglas replican las de `normalizeVenezuelanPhone` /
// `normalizeNationalId` del API (Fina/api, bncDebtorFormat.util.ts): lo que acá
// pasa, allá normaliza.
import { AbstractControl, ValidationErrors } from '@angular/forms';

import { VE_MOBILE_OPERATOR_CODES } from '../../constants/ve-mobile-prefixes';

/** Prefijos de documento que acepta BNC. */
const DOCUMENT_PREFIXES = ['V', 'E', 'J', 'G', 'P', 'C'];

/**
 * Teléfono móvil venezolano en cualquier forma usual (04141234567, 4141234567,
 * +584141234567, con espacios o guiones). Un fijo (0212…) se rechaza acá: el banco
 * no puede debitar de un número que no es de pago móvil.
 */
export const venezuelanMobileValidator = (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '').trim();
    if (!raw) return null;

    const digits = raw.replace(/\D/g, '');

    // Se quita el país (58) o el 0 de larga distancia para quedarse con los 10
    // dígitos nacionales; así "falta un dígito" no se confunde con "no es móvil".
    let national = digits;
    if (national.length > 10 && national.startsWith('58')) {
        national = national.slice(2);
    } else if (national.startsWith('0')) {
        national = national.slice(1);
    }

    if (national.length !== 10) {
        return { venezuelanMobile: 'length' };
    }

    // Operadoras de la lista única del front (sin el 0); el débito va contra un número de pago móvil.
    if (!VE_MOBILE_OPERATOR_CODES.includes(national.slice(0, 3))) {
        return { venezuelanMobile: 'prefix' };
    }

    return null;
};

/**
 * Cédula/RIF en las formas en las que la gente la escribe: "12345678",
 * "V-12.345.678", "V12345678". El rango de dígitos es amplio a propósito (hay
 * cédulas viejas de 5 dígitos): esto ataja tipeos, no suplanta la validación del
 * banco, que es la única que sabe si la cédula es la del titular de la cuenta.
 */
export const nationalIdValidator = (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '')
        .trim()
        .toUpperCase();
    if (!raw) return null;

    const digits = raw.replace(/\D/g, '');
    if (digits.length < 5 || digits.length > 9) {
        return { nationalId: 'digits' };
    }

    const letters = raw.replace(/[^A-Z]/g, '');
    if (letters.length > 1 || (letters && !DOCUMENT_PREFIXES.includes(letters))) {
        return { nationalId: 'prefix' };
    }

    return null;
};
